import { CircularProgress, Grid, IconButton, OutlinedInput, Stack, Tooltip, Typography } from "@mui/material";
import { useState } from "react";
import { cacheService, searchService, thumbnailService } from "../../../Core/Api/services";
import { ThumbnailSize } from "../../../Core/Api/Thumbnail/thumbnail-api-service";
import { ObjectType } from "../../../Core/Api/Types/object-type";
import SelectableCard from "../../Cards/SelectableCard";
import AssetThumbnail from "../../Misc/AssetThumbnail";
import PosterImage from "../../Misc/PosterImage";
import { DbAssetObjectInfos } from "../../../Core/Api/Types/db-asset-object-infos";
import { ObjectLayer } from "../../../Core/Api/Types/object-layer";
import FilterNoneOutlinedIcon from "@mui/icons-material/FilterNoneOutlined";
import LocalMoviesOutlinedIcon from "@mui/icons-material/LocalMoviesOutlined";
import React from "react";
import { SearchRequestNative } from "../../../Core/Api/Types/search-request-native";
import { useTranslation } from "react-i18next";
export interface SearchTitleOrAssetProps {
  isAsset: boolean;
  itemSelectedId?: number[] | null;
  onSelect: (object?: DbAssetObjectInfos, objects?: DbAssetObjectInfos[]) => void;
}

const SearchTitleOrAsset: React.FC<SearchTitleOrAssetProps> = (props) => {
  const { t } = useTranslation();
  const { isAsset, itemSelectedId, onSelect } = props;
  const [isLoading, setIsLoading] = useState<boolean | undefined>(undefined);
  const [searchResults, setSearchResults] = useState<DbAssetObjectInfos[]>([]);
  const [searchByTitle, setSearchByTitle] = useState<boolean>(true);

  const handleKeyDown = (event) => {
    if (event.key !== "Enter") return;
    if (event.target.value.trim() === "") return;

    setIsLoading(true);

    if (searchByTitle)
      searchService.CreateShareSearch(isAsset, event.target.value).then(function (response) {
        setIsLoading(false);
        setSearchResults(response.results || []);
      });
    else {
      let separator: string = " ";
      if (event.target.value.includes("|")) separator = "|";
      if (event.target.value.includes(",")) separator = ",";
      searchService
        .SearchBasic(
          new SearchRequestNative({
            pattern: "*",
            titleCustomerIds: event.target.value.split(separator),
            outputLayer: ObjectLayer.Title,
            hideTitleWithoutAsset: false,
          })
        )
        .then(function (response) {
          setIsLoading(false);
          if (response.results) setSearchResults(response.results || []);
        });
    }
  };

  return (
    <>
      <Typography sx={{ mb: 1 }}>
        {isAsset
          ? t("modal:searchTitleOrAsset.body.searchTitle", {
              context: searchByTitle ? "byName" : "byLabel",
              label: cacheService.GetCustomerExternalIdLabel(),
            })
          : t("modal:searchTitleOrAsset.body.searchAsset", {
              context: searchByTitle ? "byName" : "byLabel",
              label: cacheService.GetCustomerExternalIdLabel(),
            })}
      </Typography>
      <OutlinedInput
        sx={{ width: "100%" }}
        onKeyDown={handleKeyDown}
        placeholder={
          isAsset
            ? t("modal:searchTitleOrAsset.placeholder.searchTitleAndEnter", {
                context: searchByTitle ? "byName" : "byLabel",
                label: cacheService.GetCustomerExternalIdLabel(),
              })
            : t("modal:searchTitleOrAsset.placeholder.searchAssetAndEnter", {
                context: searchByTitle ? "byName" : "byLabel",
                label: cacheService.GetCustomerExternalIdLabel(),
              })
        }
        endAdornment={
          <>
            <Tooltip title={t("tooltip:title.searchByName")}>
              <IconButton onClick={() => setSearchByTitle(true)} disabled={searchByTitle}>
                <LocalMoviesOutlinedIcon
                  sx={{
                    "&.MuiSvgIcon-root": {
                      color: searchByTitle ? "grey" : "defaultColor",
                    },
                  }}
                />
              </IconButton>
            </Tooltip>
            <Tooltip title={t("tooltip:title.searchByLabel", { label: cacheService.GetCustomerExternalIdLabel() })}>
              <IconButton onClick={() => setSearchByTitle(false)} disabled={!searchByTitle}>
                <FilterNoneOutlinedIcon
                  sx={{
                    "&.MuiSvgIcon-root": {
                      color: !searchByTitle ? "grey" : "defaultColor",
                    },
                  }}
                />
              </IconButton>
            </Tooltip>
          </>
        }
      />
      {isLoading && (
        <Stack mt={3} justifyContent="center" alignItems="center">
          <CircularProgress />
        </Stack>
      )}
      {!isLoading &&
        searchResults &&
        (searchResults.length > 0 ? (
          <Stack spacing={0.75} mt={3}>
            {searchResults.map((result) => (
              <SelectableCard
                key={result.id}
                onClick={() => onSelect(result)}
                selected={itemSelectedId?.includes(result.id)}
                sx={{ pb: 1 }}
              >
                <Grid container>
                  <Grid item xs={1}>
                    {isAsset ? (
                      <AssetThumbnail type={ObjectType.Audio} />
                    ) : (
                      <PosterImage
                        img={{ src: thumbnailService.GetObjectPosterUrl(result.id, ThumbnailSize.small) }}
                        height={50}
                        width={38}
                      />
                    )}
                  </Grid>
                  <Grid item xs={11}>
                    <Typography sx={{ px: 3 }}>{result.name}</Typography>
                  </Grid>
                </Grid>
              </SelectableCard>
            ))}
          </Stack>
        ) : (
          <Typography variant="body2" textAlign={"center"} mt={3}>
            {t("modal:searchTitleOrAsset.body.noResultsFound")}
          </Typography>
        ))}
    </>
  );
};

export default SearchTitleOrAsset;
